import {useEffect, useRef, useState} from "react";
import {getGroupDistribution} from "../../../api/duckdb/getGroupDistribution";

// opóźnienie zapytania przy pisaniu wyrażenia (kliknięcia wysyłają od razu)
export const DISTRIBUTION_DELAY = 300;

const EMPTY_STATE = {
    profile: null,
    preview: null,
    previewLevel: null,
    loading: false,
    error: false,
};

/**
 * Rozkład kolumny (profile) i podgląd grup (preview) dla modala poziomu grupowania.
 * Profil nie zależy od konfiguracji poziomu - pobierany przy otwarciu i zmianie grupy nadrzędnej
 * (razem z podglądem, żeby `BarGroups` pasowały do `Bars`); zmiana konfiguracji pobiera tylko podgląd.
 * Poprzednie zapytanie jest anulowane - odpowiedzi przy szybkim pisaniu mogą przyjść w innej kolejności.
 * @param {Object} params
 * @param {string} params.resultId
 * @param {Array} [params.filter]
 * @param {Array} params.groupFields poziomy z roboczą wersją edytowanego poziomu
 * @param {number} params.level edytowany poziom (od 0)
 * @param {*} [params.parentKey] klucz grupy poziomu wyżej
 * @param {Object} [params.groupSort] kolejność grup przy "Każdej wartości"
 * @param {boolean} params.previewEnabled konfiguracja poprawna - błędnej nie wysyłamy
 * @param {string} [params.typing] wpisywany tekst (wyrażenie) - tylko jego zmiana czeka na koniec pisania,
 *        ładowanie widać od razu po zmianie
 * @returns {{profile: Object|null, preview: Object|null, previewLevel: *, loading: boolean, error: boolean}}
 * `previewLevel` - poziom `GroupFields[level]`, dla którego jest podgląd
 */
export default function useGroupDistribution({
    resultId,
    filter,
    groupFields,
    level,
    parentKey,
    groupSort,
    previewEnabled,
    typing,
}) {
    const [state, setState] = useState(EMPTY_STATE);
    // profil zależy od danych i grup nadrzędnych, nie od edytowanego poziomu
    const profileKey = JSON.stringify({
        resultId,
        filter,
        level,
        parentKey,
        parents: groupFields.slice(0, level),
    });
    const requestKey = JSON.stringify({
        profileKey,
        groupFields,
        groupSort,
        previewEnabled,
    });
    const loadedProfileKeyRef = useRef(null);
    const typingRef = useRef(typing);

    useEffect(() => {
        if (!resultId) return undefined;
        const withProfile = loadedProfileKeyRef.current !== profileKey;
        const include = [];
        if (withProfile) include.push("profile");
        if (previewEnabled) include.push("preview");
        const typed = typingRef.current !== typing;
        typingRef.current = typing;
        if (include.length === 0) {
            // np. błędne wyrażenie - nic nie idzie, nic się nie ładuje
            setState((prev) =>
                prev.loading ? {...prev, loading: false} : prev
            );
            return undefined;
        }
        const controller = new AbortController();
        // ładowanie od zmiany, nie od wysłania; czeka tylko pisanie
        setState((prev) =>
            prev.loading && !prev.error
                ? prev
                : {...prev, loading: true, error: false}
        );
        const delay = typed && !withProfile ? DISTRIBUTION_DELAY : 0;
        const timeout = setTimeout(async () => {
            try {
                const response = await getGroupDistribution(
                    {
                        ResultID: resultId,
                        Filter: filter,
                        GroupFields: groupFields,
                        Level: level,
                        ParentKey: parentKey ?? undefined,
                        GroupSort: groupSort,
                        Include: include,
                    },
                    undefined,
                    controller.signal
                );
                if (controller.signal.aborted) return;
                if (withProfile) loadedProfileKeyRef.current = profileKey;
                const withPreview = previewEnabled || withProfile;
                setState((prev) => ({
                    profile: withProfile
                        ? (response?.Profile ?? null)
                        : prev.profile,
                    // stary podgląd z innym profilem nie pasuje do słupków
                    preview: withPreview
                        ? (response?.Preview ?? null)
                        : prev.preview,
                    previewLevel: withPreview
                        ? groupFields[level]
                        : prev.previewLevel,
                    loading: false,
                    error: false,
                }));
            } catch (e) {
                if (controller.signal.aborted) return;
                setState((prev) => ({...prev, loading: false, error: true}));
            }
        }, delay);
        return () => {
            clearTimeout(timeout);
            controller.abort();
        };
        // requestKey obejmuje wszystkie pola zapytania
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestKey]);

    return state;
}
